import { useMemo, useState } from "react";
import { useHistory } from "react-router";
import { toast } from "react-toastify";
import { FiTrash2 } from "react-icons/fi";
import { PathName } from "../../../constants/PathName";
import { Constants } from "../../../constants/Constants";
import {
	useDeleteFormBuilderSelectMutation,
	useListFormBuilderSelectsQuery,
} from "../../../generated/nuggetKmsApi/graphql";

const PAGE_SIZE = 10;

export const SelectsTable = () => {
	const history = useHistory();
	const [search, setSearch] = useState("");
	const [page, setPage] = useState(1);

	const { data, loading, refetch } = useListFormBuilderSelectsQuery({
		variables: {
			input: {
				search: search.trim() ? search.trim() : null,
				pagination: { limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE },
			},
		},
		context: { clientName: Constants.APOLLO_CLIENT_NUGGET_KMS_API_NAME },
		fetchPolicy: "network-only",
	});

	const [deleteSelect, { loading: deleting }] = useDeleteFormBuilderSelectMutation({
		context: { clientName: Constants.APOLLO_CLIENT_NUGGET_KMS_API_NAME },
		onCompleted: () => {
			toast.success("Select deleted.");
			refetch();
		},
		onError: (error) => {
			toast.error(error.message || "Failed to delete select.");
		},
	});

	const rows = data?.listFormBuilderSelects.rows ?? [];
	const totalCount = data?.listFormBuilderSelects.count ?? 0;
	const totalPages = useMemo(() => Math.max(1, Math.ceil(totalCount / PAGE_SIZE)), [totalCount]);

	const handleRowClick = (id: number) => {
		history.push(PathName.FORM_BUILDER_SELECTS_EDIT(String(id)));
	};

	const handleDelete = (event: React.MouseEvent<HTMLButtonElement>, id: number) => {
		event.stopPropagation();
		if (!window.confirm("Delete this select? This action cannot be undone.")) {
			return;
		}
		deleteSelect({ variables: { id } });
	};

	const handleSearchChange = (value: string) => {
		setSearch(value);
		setPage(1);
	};

	return (
		<div className="formBuilder-table">
			<div className="formBuilder-tableHeader row">
				<div className="col-xs-12 col-sm-6">
					<input
						type="text"
						placeholder="Filter by name"
						value={search}
						onChange={(event) => handleSearchChange(event.target.value)}
					/>
				</div>
				<div className="col-xs-12 col-sm-6 text-right">
					<button
						type="button"
						className="formBuilder-button formBuilder-button--primary"
						onClick={() => history.push(PathName.FORM_BUILDER_SELECTS_ADD)}
					>
						+ Add Select
					</button>
				</div>
			</div>
			<table>
				<thead>
					<tr>
						<th>ID</th>
						<th>Name</th>
						<th>Updated</th>
						<th />
					</tr>
				</thead>
				<tbody>
					{loading ? (
						<tr>
							<td colSpan={4}>Loading...</td>
						</tr>
					) : rows.length === 0 ? (
						<tr>
							<td colSpan={4}>No selects found.</td>
						</tr>
					) : (
						rows.map((row) => (
							<tr key={row.id} onClick={() => handleRowClick(row.id)}>
								<td>{row.id}</td>
								<td>{row.name}</td>
								<td>{new Date(row.updatedAt).toLocaleString()}</td>
								<td className="table-actions">
									<button
										type="button"
										className="formBuilder-button formBuilder-button--danger"
										onClick={(event) => handleDelete(event, row.id)}
										disabled={deleting}
										aria-label="Delete select"
									>
										<FiTrash2 />
									</button>
								</td>
							</tr>
						))
					)}
				</tbody>
			</table>
			<div className="formBuilder-pagination">
				<button
					type="button"
					onClick={() => setPage((prev) => Math.max(1, prev - 1))}
					disabled={page === 1}
				>
					Prev
				</button>
				<span>
					Page {page} of {totalPages}
				</span>
				<button
					type="button"
					onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
					disabled={page >= totalPages}
				>
					Next
				</button>
			</div>
		</div>
	);
};
